import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { ArrowLeft, Eye, Users, CalendarDays, Database, TrendingUp, TrendingDown, RefreshCw, Car, Trophy } from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell,
} from "recharts";

const GREEN = "#22C55E";
const BLUE = "#38BDF8";

const PAGE_LABELS = {
  "/": "Início",
  "/blog": "Blog",
  "/carros-usados-ovar": "Carros usados · Ovar",
  "/carros-usados-aveiro": "Carros usados · Aveiro",
  "/automoveis-usados-porto": "Automóveis · Porto",
};
const prettyPath = (p) => {
  if (PAGE_LABELS[p]) return PAGE_LABELS[p];
  if (p.startsWith("/viatura/")) return "Ficha de viatura";
  if (p.startsWith("/blog/")) return "Artigo do blog";
  return p;
};

const delta = (cur, prev) => {
  if (prev > 0) return Math.round(((cur - prev) / prev) * 100);
  return cur > 0 ? 100 : 0;
};

const Kpi = ({ icon: Icon, label, value, sub, deltaValue, tid }) => {
  const up = deltaValue >= 0;
  return (
    <div data-testid={tid} className="relative overflow-hidden rounded-2xl bg-zinc-900 border border-white/10 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">{label}</p>
          <p className="font-heading font-black text-4xl text-white mt-2 tabular-nums">{value}</p>
        </div>
        <span className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </span>
      </div>
      <div className="flex items-center gap-2 mt-3">
        {deltaValue != null && (
          <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${up ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
            {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />} {up ? "+" : ""}{deltaValue}%
          </span>
        )}
        <span className="text-zinc-500 text-xs">{sub}</span>
      </div>
      <span className="pointer-events-none absolute -right-6 -bottom-8 w-28 h-28 rounded-full bg-primary/10 blur-2xl" />
    </div>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-xl bg-zinc-900 border border-white/10 px-3 py-2 shadow-xl">
      <p className="text-zinc-300 text-xs font-semibold mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="text-xs" style={{ color: p.color }}>{p.name}: <span className="font-bold">{p.value}</span></p>
      ))}
    </div>
  );
};

class Boundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) { console.error("Analytics render error:", err); }
  render() { return this.state.hasError ? this.props.fallback : this.props.children; }
}

export default function AdminAnalytics() {
  const { user, checking } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => { if (!checking && !user) navigate("/login"); }, [checking, user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get("/stats");
      setStats(data);
    } catch (e) {
      console.error("Failed to load stats:", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  if (checking || !user) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-950"><div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  const daily = stats?.daily || [];
  const topPages = (stats?.top_pages || []).map((t) => ({ ...t, name: prettyPath(t.path) }));
  const topVehicles = (stats?.top_vehicles || []).map((v) => ({ ...v, name: `${v.brand} ${v.model}` }));

  const errorCard = (
    <div data-testid="analytics-error" className="rounded-2xl bg-zinc-900 border border-white/10 p-10 text-center">
      <p className="text-white font-heading font-bold text-lg">Não foi possível carregar as estatísticas</p>
      <p className="text-zinc-400 text-sm mt-2">Verifique a ligação e tente novamente.</p>
      <Button onClick={load} className="mt-5 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"><RefreshCw className="w-4 h-4 mr-1.5" /> Tentar novamente</Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <header className="border-b border-white/10 sticky top-0 z-40 bg-zinc-950/80 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <button data-testid="analytics-back" onClick={() => navigate("/admin")} className="flex items-center gap-2 text-sm font-semibold text-zinc-300 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Voltar ao painel
          </button>
          <Button data-testid="analytics-refresh" variant="outline" onClick={load} className="rounded-full font-semibold border-white/20 bg-transparent text-white hover:bg-white hover:text-zinc-950 h-9">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Atualizar
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="mb-8">
          <span className="text-primary font-bold text-xs tracking-[0.25em] uppercase">Análise</span>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mt-2">Estatísticas de visitas</h1>
          <p className="text-zinc-400 text-sm mt-2">Dados dos últimos 7 dias · comparado com os 7 dias anteriores.</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <div key={`sk-${i}`} className="h-32 rounded-2xl bg-zinc-900 animate-pulse" />)}
          </div>
        ) : (error || !stats) ? errorCard : (
          <Boundary fallback={errorCard}>
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi tid="kpi-views" icon={Eye} label="Visitas (7 dias)" value={stats.total_views_7d} deltaValue={delta(stats.total_views_7d, stats.prev_views_7d)} sub="vs. semana anterior" />
              <Kpi tid="kpi-visitors" icon={Users} label="Visitantes únicos" value={stats.unique_visitors_7d} deltaValue={delta(stats.unique_visitors_7d, stats.prev_unique_7d)} sub="vs. semana anterior" />
              <Kpi tid="kpi-today" icon={CalendarDays} label="Visitas hoje" value={stats.today_views} sub="desde as 00h00" />
              <Kpi tid="kpi-total" icon={Database} label="Total acumulado" value={stats.total_all_time} sub="desde sempre" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
            <div data-testid="kpi-top-vehicle" className="lg:col-span-2 rounded-2xl bg-gradient-to-r from-zinc-900 to-zinc-900/60 border border-primary/25 p-5 flex items-center gap-5">
              <div className="w-28 h-20 rounded-xl overflow-hidden bg-zinc-800 shrink-0 flex items-center justify-center">
                {stats.most_viewed_vehicle?.image ? (
                  <img src={fileUrl(stats.most_viewed_vehicle.image)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Car className="w-8 h-8 text-zinc-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-primary text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> Viatura mais vista (7 dias)</p>
                {stats.most_viewed_vehicle ? (
                  <>
                    <p className="font-heading font-black text-2xl text-white truncate mt-1">{stats.most_viewed_vehicle.brand} {stats.most_viewed_vehicle.model}</p>
                    <p className="text-zinc-400 text-sm mt-0.5"><span className="text-primary font-bold">{stats.most_viewed_vehicle.views}</span> visualizações{stats.most_viewed_vehicle.sold ? " · Vendido" : ""}</p>
                  </>
                ) : (
                  <p className="text-zinc-500 text-sm mt-2">Ainda sem visualizações de fichas de viatura.</p>
                )}
              </div>
              {stats.most_viewed_vehicle && (
                <Button data-testid="top-vehicle-open" onClick={() => navigate(`/viatura/${stats.most_viewed_vehicle.id}`)} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shrink-0">Ver ficha</Button>
              )}
            </div>

              <div data-testid="chart-top-vehicles" className="rounded-2xl bg-zinc-900 border border-white/10 p-5">
                <h2 className="font-heading font-bold text-lg">Top 3 viaturas</h2>
                <p className="text-zinc-500 text-xs mb-4">Fichas mais vistas (7 dias)</p>
                {topVehicles.length === 0 ? (
                  <p className="text-zinc-500 text-sm py-10 text-center">Ainda sem dados.</p>
                ) : (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={topVehicles} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" horizontal={false} />
                        <XAxis type="number" stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} width={120} />
                        <Tooltip content={<ChartTooltip />} cursor={{ fill: "#ffffff08" }} />
                        <Bar dataKey="views" name="Visualizações" radius={[0, 6, 6, 0]}>
                          {topVehicles.map((entry, i) => <Cell key={entry.id} fill={i === 0 ? GREEN : "#3f8f5c"} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
              <div data-testid="chart-daily" className="lg:col-span-2 rounded-2xl bg-zinc-900 border border-white/10 p-5">
                <h2 className="font-heading font-bold text-lg">Visitas por dia</h2>
                <p className="text-zinc-500 text-xs mb-4">Visitas e visitantes únicos nos últimos 7 dias</p>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={daily} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={GREEN} stopOpacity={0.55} />
                          <stop offset="100%" stopColor={GREEN} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gVis" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={BLUE} stopOpacity={0.4} />
                          <stop offset="100%" stopColor={BLUE} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" vertical={false} />
                      <XAxis dataKey="label" stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} />
                      <YAxis stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12, color: "#a1a1aa" }} />
                      <Area type="monotone" dataKey="views" name="Visitas" stroke={GREEN} strokeWidth={2.5} fill="url(#gViews)" />
                      <Area type="monotone" dataKey="visitors" name="Visitantes" stroke={BLUE} strokeWidth={2.5} fill="url(#gVis)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div data-testid="chart-top" className="rounded-2xl bg-zinc-900 border border-white/10 p-5">
                <h2 className="font-heading font-bold text-lg">Páginas mais vistas</h2>
                <p className="text-zinc-500 text-xs mb-4">Últimos 7 dias</p>
                {topPages.length === 0 ? (
                  <p className="text-zinc-500 text-sm py-10 text-center">Ainda sem dados suficientes.</p>
                ) : (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={topPages} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" horizontal={false} />
                        <XAxis type="number" stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} width={110} />
                        <Tooltip content={<ChartTooltip />} cursor={{ fill: "#ffffff08" }} />
                        <Bar dataKey="views" name="Visitas" radius={[0, 6, 6, 0]}>
                          {topPages.map((entry, i) => <Cell key={entry.path} fill={i === 0 ? GREEN : "#3f8f5c"} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            <p className="text-zinc-600 text-xs mt-6">As visitas ao painel de administração não são contabilizadas. Contagem anónima e sem cookies de terceiros (RGPD).</p>
          </>
          </Boundary>
        )}
      </main>
    </div>
  );
}
